import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, HandCoins, Check, X, IndianRupee, ListChecks, Trash2, Upload, Paperclip, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { openLoanAgreement } from "@/lib/loanAgreementTemplate";
import { openServiceCommitment } from "@/lib/serviceCommitmentTemplate";
import { TOKEN_KEY } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (path) => `${API}/api/files/${path}?token=${encodeURIComponent(localStorage.getItem(TOKEN_KEY) || "")}`;

const MAX_PRINCIPAL = 220000;
const MAX_TENURE = 72;
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const inr2 = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-blue-100 text-blue-800 border-blue-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  disbursed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed: "bg-slate-200 text-slate-700 border-slate-300",
};

export default function Loans() {
  const { user } = useAuth();
  const isAdmin = ["founder", "admin"].includes(user?.role);
  const qc = useQueryClient();

  const [applyOpen, setApplyOpen] = useState(false);
  const [principal, setPrincipal] = useState(50000);
  const [tenure, setTenure] = useState(12);
  const [quote, setQuote] = useState(null);
  const [schedLoan, setSchedLoan] = useState(null);
  const [disburse, setDisburse] = useState({ open: false, loan: null, date: new Date().toISOString().slice(0, 10) });
  const [reject, setReject] = useState({ open: false, loan: null, note: "" });

  const { data: loans = [], isLoading } = useQuery({
    queryKey: ["loans"],
    queryFn: async () => (await api.get("/loans")).data,
  });

  const myActive = !isAdmin && loans.find((l) => ["pending", "approved", "disbursed"].includes(l.status));

  // live EMI quote
  useEffect(() => {
    if (!applyOpen) return;
    const p = Number(principal), t = Number(tenure);
    if (!p || !t || p > MAX_PRINCIPAL || t > MAX_TENURE) { setQuote(null); return; }
    let cancel = false;
    const id = setTimeout(() => {
      api.post("/loans/quote", { principal: p, tenure_months: t })
        .then((r) => { if (!cancel) setQuote(r.data); })
        .catch(() => { if (!cancel) setQuote(null); });
    }, 300);
    return () => { cancel = true; clearTimeout(id); };
  }, [principal, tenure, applyOpen]);

  const apply = useMutation({
    mutationFn: () => api.post("/loans", { principal: Number(principal), tenure_months: Number(tenure) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["loans"] }); setApplyOpen(false); toast.success("Loan application submitted for review"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not submit application"),
  });
  const review = useMutation({
    mutationFn: ({ id, action, note }) => api.post(`/loans/${id}/review`, { action, note }),
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ["loans"] }); setReject({ open: false, loan: null, note: "" }); toast.success(v.action === "approve" ? "Loan approved" : "Loan rejected"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Action failed"),
  });
  const doDisburse = useMutation({
    mutationFn: () => api.post(`/loans/${disburse.loan.loan_id}/disburse`, { disburse_date: disburse.date }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["loans"] }); setDisburse({ open: false, loan: null, date: new Date().toISOString().slice(0, 10) }); toast.success("Loan disbursed & agreement generated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Disbursement failed"),
  });
  const pay = useMutation({
    mutationFn: (id) => api.post(`/loans/${id}/record-payment`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["loans"] }); toast.success("EMI payment recorded"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not record payment"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/loans/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["loans"] }); toast.success("Loan deleted"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });
  const fileRef = useRef(null);
  const [uploadLoanId, setUploadLoanId] = useState(null);
  const uploadSigned = useMutation({
    mutationFn: ({ id, file }) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.post(`/loans/${id}/signed-copy`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["loans"] }); toast.success("Signed agreement uploaded"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });
  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f && uploadLoanId) uploadSigned.mutate({ id: uploadLoanId, file: f });
    e.target.value = "";
  };

  if (isLoading)
    return (<div><PageHeader title="Employee Loans" subtitle="Loan facility" /><Skeleton className="h-64 rounded-lg" /></div>);

  return (
    <div data-testid="loans-page">
      <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onPickFile} data-testid="loan-signed-file-input" />
      <PageHeader
        title="Employee Loans"
        subtitle={isAdmin ? "Review, approve & manage staff loans" : "Apply for and track your loan"}
      >
        {!isAdmin && (
          <Button
            onClick={() => setApplyOpen(true)}
            disabled={!!myActive}
            data-testid="apply-loan-btn"
            className="bg-navy hover:bg-navy-900 text-white rounded-lg"
          >
            <Plus className="mr-2 h-4 w-4" /> Apply for Loan
          </Button>
        )}
      </PageHeader>

      {!isAdmin && myActive && (
        <div className="mb-4 rounded-lg bg-gold/15 px-4 py-2 text-sm text-navy" data-testid="active-loan-note">
          You have an active loan ({myActive.status}). You can apply again once it is fully repaid.
        </div>
      )}

      <SectionCard title={isAdmin ? "All Loans" : "My Loans"} testId="loans-list">
        {loans.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="loans-empty">
            <div className="rounded-full bg-navy/5 p-4 mb-3"><HandCoins className="h-8 w-8 text-navy" /></div>
            <p className="text-sm font-medium text-slate-500">No loans yet</p>
            <p className="mt-1 text-xs text-slate-400">
              {isAdmin ? "Applications from employees will appear here." : "Apply for a loan to get started (up to ₹2,20,000)."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead>Employee</TableHead>}
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Tenure</TableHead>
                  <TableHead className="text-right">EMI</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loans.map((l) => (
                  <TableRow key={l.loan_id} data-testid={`loan-row-${l.loan_id}`}>
                    {isAdmin && <TableCell>{l.employee_name} <span className="text-xs text-slate-400">({l.employee_id})</span></TableCell>}
                    <TableCell className="text-right font-medium text-navy">{inr(l.principal)}</TableCell>
                    <TableCell className="text-right">{l.tenure_months} mo</TableCell>
                    <TableCell className="text-right">{inr(l.emi)}</TableCell>
                    <TableCell className="text-right">
                      {l.status === "disbursed" || l.status === "closed" ? (
                        <div>
                          <div className="text-sm font-semibold">{inr(l.outstanding)}</div>
                          <Progress value={(l.paid_emis / l.tenure_months) * 100} className="h-1.5 mt-1 w-24 ml-auto" />
                          <div className="text-[10px] text-slate-400 mt-0.5">{l.paid_emis}/{l.tenure_months} EMIs paid</div>
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell><Badge variant="outline" className={STATUS[l.status]}>{l.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1 flex-wrap">
                        <Button size="sm" variant="ghost" onClick={() => setSchedLoan(l)} data-testid={`loan-schedule-${l.loan_id}`} title="Repayment schedule">
                          <ListChecks className="h-4 w-4 text-navy" />
                        </Button>
                        {(l.status === "disbursed" || l.status === "closed" || l.status === "approved") && (
                          <Button size="sm" variant="ghost" onClick={() => openLoanAgreement(l)} data-testid={`loan-agreement-${l.loan_id}`} title="Loan agreement">
                            <FileText className="h-4 w-4 text-gold-dark" />
                          </Button>
                        )}
                        {(l.status === "disbursed" || l.status === "closed" || l.status === "approved") && (
                          <Button size="sm" variant="ghost" onClick={() => openServiceCommitment(l)} data-testid={`loan-service-commitment-${l.loan_id}`} title="Service Commitment agreement">
                            <ShieldCheck className="h-4 w-4 text-navy" />
                          </Button>
                        )}
                        {isAdmin && (l.status === "disbursed" || l.status === "closed" || l.status === "approved") && (
                          <Button size="sm" variant="ghost" onClick={() => { setUploadLoanId(l.loan_id); fileRef.current?.click(); }} data-testid={`loan-upload-signed-${l.loan_id}`} title="Upload signed copy">
                            <Upload className="h-4 w-4 text-navy" />
                          </Button>
                        )}
                        {l.signed_copy_path && (
                          <a href={fileUrl(l.signed_copy_path)} target="_blank" rel="noreferrer" data-testid={`loan-signed-view-${l.loan_id}`} title="View signed copy" className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-slate-100">
                            <Paperclip className="h-4 w-4 text-emerald-600" />
                          </a>
                        )}
                        {isAdmin && l.status === "pending" && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => review.mutate({ id: l.loan_id, action: "approve" })} data-testid={`loan-approve-${l.loan_id}`} title="Approve">
                              <Check className="h-4 w-4 text-emerald-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setReject({ open: true, loan: l, note: "" })} data-testid={`loan-reject-${l.loan_id}`} title="Reject">
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </>
                        )}
                        {isAdmin && l.status === "approved" && (
                          <Button size="sm" onClick={() => setDisburse({ open: true, loan: l, date: new Date().toISOString().slice(0, 10) })} data-testid={`loan-disburse-${l.loan_id}`} className="h-8 bg-navy text-white">
                            <IndianRupee className="h-3.5 w-3.5 mr-1" /> Disburse
                          </Button>
                        )}
                        {isAdmin && l.status === "disbursed" && (
                          <Button size="sm" onClick={() => pay.mutate(l.loan_id)} data-testid={`loan-pay-${l.loan_id}`} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white">
                            Record EMI
                          </Button>
                        )}
                        {isAdmin && ["pending", "approved", "rejected", "disbursed", "closed"].includes(l.status) && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" data-testid={`loan-delete-${l.loan_id}`} title="Delete"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Delete this loan?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {l.employee_name} — {inr(l.principal)} ({l.status}). This permanently removes the loan and its record. This cannot be undone.
                                </AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => del.mutate(l.loan_id)} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>

      {/* Apply dialog (employee) */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-navy">Apply for a Loan</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
              Max amount <b>₹2,20,000</b> · Max tenure <b>72 months</b> · Interest <b>24% p.a.</b> (reducing balance). EMI is deducted from your salary as per the signed agreement. You commit to serve the Enterprise for the full tenure (service bond).
            </div>
            <div>
              <Label>Loan Amount (max ₹2,20,000)</Label>
              <Input type="number" max={MAX_PRINCIPAL} min={1} data-testid="loan-amount-input" value={principal} onChange={(e) => setPrincipal(e.target.value)} />
            </div>
            <div>
              <Label>Tenure (months, max 72)</Label>
              <Input type="number" max={MAX_TENURE} min={1} data-testid="loan-tenure-input" value={tenure} onChange={(e) => setTenure(e.target.value)} />
            </div>
            {Number(principal) > MAX_PRINCIPAL && <p className="text-xs text-red-600">Amount cannot exceed ₹2,20,000.</p>}
            {Number(tenure) > MAX_TENURE && <p className="text-xs text-red-600">Tenure cannot exceed 72 months.</p>}
            {quote && (
              <div className="grid grid-cols-3 gap-3" data-testid="loan-quote">
                <div className="rounded-lg bg-gold/15 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Monthly EMI</div>
                  <div className="font-heading text-lg font-bold text-navy">{inr(quote.emi)}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Total Interest</div>
                  <div className="font-heading text-lg font-bold text-navy">{inr(quote.total_interest)}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Total Payable</div>
                  <div className="font-heading text-lg font-bold text-navy">{inr(quote.total_payable)}</div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)}>Cancel</Button>
            <Button onClick={() => apply.mutate()} disabled={apply.isPending || !quote} data-testid="loan-submit-btn" className="bg-navy text-white">Submit Application</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disburse dialog (admin) */}
      <Dialog open={disburse.open} onOpenChange={(o) => setDisburse((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-navy">Disburse Loan</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            {disburse.loan && `${disburse.loan.employee_name} — ${inr(disburse.loan.principal)} over ${disburse.loan.tenure_months} months (EMI ${inr(disburse.loan.emi)}).`}
          </p>
          <div>
            <Label>Disbursement Date</Label>
            <Input type="date" data-testid="disburse-date-input" value={disburse.date} onChange={(e) => setDisburse((d) => ({ ...d, date: e.target.value }))} />
          </div>
          <p className="text-xs text-slate-400">The repayment schedule due dates and the loan agreement will be generated from this date.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisburse((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button onClick={() => doDisburse.mutate()} disabled={doDisburse.isPending} data-testid="disburse-confirm-btn" className="bg-navy text-white">Disburse & Generate Agreement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog (admin) */}
      <Dialog open={reject.open} onOpenChange={(o) => setReject((r) => ({ ...r, open: o }))}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-navy">Reject Loan Application</DialogTitle></DialogHeader>
          <div>
            <Label>Reason (optional)</Label>
            <Input data-testid="reject-note-input" value={reject.note} onChange={(e) => setReject((r) => ({ ...r, note: e.target.value }))} placeholder="e.g. Existing dues pending" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReject({ open: false, loan: null, note: "" })}>Cancel</Button>
            <Button onClick={() => review.mutate({ id: reject.loan.loan_id, action: "reject", note: reject.note })} data-testid="reject-confirm-btn" className="bg-red-600 hover:bg-red-700 text-white">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog open={!!schedLoan} onOpenChange={(o) => !o && setSchedLoan(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-navy">Repayment Schedule</DialogTitle></DialogHeader>
          {schedLoan && (
            <div>
              <div className="grid grid-cols-4 gap-2 mb-3 text-center text-xs">
                <div className="rounded bg-slate-50 p-2"><div className="text-slate-400">Amount</div><b>{inr(schedLoan.principal)}</b></div>
                <div className="rounded bg-slate-50 p-2"><div className="text-slate-400">EMI</div><b>{inr(schedLoan.emi)}</b></div>
                <div className="rounded bg-slate-50 p-2"><div className="text-slate-400">Interest</div><b>{inr(schedLoan.total_interest)}</b></div>
                <div className="rounded bg-slate-50 p-2"><div className="text-slate-400">Total</div><b>{inr(schedLoan.total_payable)}</b></div>
              </div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>#</TableHead><TableHead>Due</TableHead>
                  <TableHead className="text-right">EMI</TableHead><TableHead className="text-right">Principal</TableHead>
                  <TableHead className="text-right">Interest</TableHead><TableHead className="text-right">Balance</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(schedLoan.schedule || []).map((s) => (
                    <TableRow key={s.month}>
                      <TableCell>{s.month}</TableCell>
                      <TableCell>{s.due_date ? new Date(s.due_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</TableCell>
                      <TableCell className="text-right">{inr2(s.emi)}</TableCell>
                      <TableCell className="text-right">{inr2(s.principal)}</TableCell>
                      <TableCell className="text-right">{inr2(s.interest)}</TableCell>
                      <TableCell className="text-right">{inr2(s.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
